/**
 * Natural-language → formula generation.
 *
 * Production asks the backend, which knows the object's real fields and
 * validates the candidate expression against the formula grammar before
 * returning it. Lovable preview has no backend, so it falls back to the local
 * keyword interpreter (`interpret-formula.ts`) — deterministic and instant, but
 * only able to recognize a handful of phrasings.
 *
 * Either way the caller gets an OUTCOME, never a silent partial result. A
 * description the language can't express ("amount times two", "revenue this
 * quarter") comes back as `status: "unsupported"` with a message to show; it must
 * not drop the user into the guided builder, because a builder seeded from
 * nothing reads as a half-built formula with no explanation.
 */

import { useCallback, useState } from 'react';
import { useGenerateFormulaLazyQuery } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from './use-workspace-id';
import i18n from '@/lib/i18n';
import { isLovablePreview } from '@/lib/utils/environment';
import { interpretFormula, unsupportedConceptIn } from '@/lib/utils/interpret-formula';
import {
  resolveFormulaState,
  tryParseFormula,
  type FormulaFieldOption,
  type FormulaState,
} from '@/lib/utils/formula-fields';

/**
 * Shown when nothing more specific is known about the failure.
 *
 * A function, not a constant: a `t()` at module scope would capture the language the bundle
 * loaded in and never update. The three example phrasings inside it stay English in every
 * language on purpose — they are INPUT to the generator, and both the backend prompt and the
 * preview-mode interpreter (`interpret-formula.ts`) recognise English wording only, so a
 * translated example would be advice that cannot work.
 */
export function genericFormulaFailure(): string {
  return i18n.t("shared.formula.genericFailure");
}

export interface GeneratedFormula {
  status: 'ok';
  state: FormulaState;
  /** Parts of the description that couldn't be mapped onto a real field. */
  gaps: string[];
  summary?: string;
}

/** The description can't be turned into a formula — show `message`, build nothing. */
export interface UnsupportedFormula {
  status: 'unsupported';
  message: string;
}

export type FormulaGenerationOutcome = GeneratedFormula | UnsupportedFormula;

export interface GenerateFormulaParams {
  description: string;
  /** Backend object type: "Contact" | "Account" | "Deal". */
  objectType?: string | null;
  /** Fields the expression may reference, for resolving the returned keys. */
  fields: FormulaFieldOption[];
  /** Fields on the far side of the relation, for a LOOKUP result. */
  lookupFields?: FormulaFieldOption[];
}

/**
 * Why we couldn't build a formula, most specific first: the backend's own
 * message, then the grammar limit the description ran into, then generic advice.
 */
function failureMessage(description: string, backendError?: string | null): string {
  const fromBackend = backendError?.trim();
  if (fromBackend) return fromBackend;
  return unsupportedConceptIn(description) ?? genericFormulaFailure();
}

export const useFormulaGeneration = () => {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();
  const [generateFormulaQuery] = useGenerateFormulaLazyQuery();
  const [generating, setGenerating] = useState(false);

  const generate = useCallback(
    async ({
      description,
      objectType,
      fields,
      lookupFields = [],
    }: GenerateFormulaParams): Promise<FormulaGenerationOutcome | null> => {
      const text = description.trim();
      if (!text) return null;

      // Preview (and any caller without a workspace/object type): local
      // interpreter, no network request.
      if (preview || !workspaceId || !objectType) {
        const local = interpretFormula(text, fields);
        if (!local) return { status: 'unsupported', message: failureMessage(text) };
        return {
          status: 'ok',
          state: {
            kind: local.kind,
            slots: local.slots,
            args: local.slots.a ? [local.slots.a] : [''],
            ifState: local.ifState,
          },
          gaps: local.gaps,
          summary: local.summary,
        };
      }

      setGenerating(true);
      try {
        const result = await generateFormulaQuery({
          variables: {
            workspaceId: parseInt(workspaceId),
            objectType,
            description: text,
          },
        });

        const payload = result.data?.generateFormula;
        const expression = payload?.expression?.trim();
        if (!expression) {
          // The resolver reports its own reason in `error` ("could not build a
          // formula from that description…"); dropping it on the floor was what
          // left the builder with nothing to say.
          return { status: 'unsupported', message: failureMessage(text, payload?.error) };
        }

        // The backend grammar is wider than the builder's five shapes — a bare
        // field reference validates and stores fine, and the agent's prompt tells
        // it to fall back to one when the request can't be expressed. That is a
        // valid expression the builder cannot RENDER, so say so rather than
        // opening on an empty AVERAGE.
        const parsed = tryParseFormula(expression);
        if (!parsed) {
          return {
            status: 'unsupported',
            message:
              unsupportedConceptIn(text)
              ?? `That description came back as "${expression}", which the guided builder `
                 + 'can\'t show. It needs one of SUM, AVERAGE, COUNT, DATEDIF, IF/THEN or LOOKUP.',
          };
        }

        // Resolve the returned references against the fields actually on offer,
        // so an unknown key surfaces as a broken token instead of being saved.
        const { state, unresolved } = resolveFormulaState(parsed, fields, lookupFields);
        const gaps = (payload?.gaps ?? []).filter((g): g is string => !!g);
        return {
          status: 'ok',
          state,
          gaps: [
            ...gaps,
            ...unresolved.map(t => `"${t}" doesn't match a field on this object`),
          ],
        };
      } finally {
        setGenerating(false);
      }
    },
    [preview, workspaceId, generateFormulaQuery],
  );

  return { generate, generating };
};
